import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useFetch } from '@/modules/core/composables/useFetch'
import { useRoute } from 'vue-router'
import type { TeamMember, LinkedinLookupResult, LinkedinLookupError } from '../types'

export const useTeamMemberStore = defineStore('connecting/teamMemberStore', () => {
  const route = useRoute()
  const members = ref<TeamMember[]>([])
  const loading = ref(false)

  const getBaseUrl = (candidateId: string) =>
    `/v4/${route.params.slug || 'connections'}/connecting/candidates/${candidateId}/team-members`

  const fetchTeamMembers = async (candidateId: string) => {
    loading.value = true
    try {
      const data = await useFetch(getBaseUrl(candidateId)).get<TeamMember[]>()
      members.value = data
    } finally {
      loading.value = false
    }
  }

  const createTeamMember = async (candidateId: string, payload: FormData) => {
    const data = await useFetch(getBaseUrl(candidateId)).post<TeamMember>(payload)
    members.value.push(data)
    return data
  }

  const updateTeamMember = async (candidateId: string, memberId: string, payload: FormData) => {
    const data = await useFetch(`${getBaseUrl(candidateId)}/${memberId}`).post<TeamMember>(payload)
    const index = members.value.findIndex((m) => m.id === memberId)
    if (index !== -1) {
      members.value[index] = data
    }
    return data
  }

  const deleteTeamMember = async (candidateId: string, memberId: string) => {
    await useFetch(`${getBaseUrl(candidateId)}/${memberId}/delete`).post()
    members.value = members.value.filter((m) => m.id !== memberId)
  }

  const reorderTeamMembers = async (candidateId: string, memberIds: string[]) => {
    await useFetch(`${getBaseUrl(candidateId)}/reorder`).post({ member_ids: memberIds })
    // Update local positions
    memberIds.forEach((id, index) => {
      const member = members.value.find((m) => m.id === id)
      if (member) {
        member.position = index
      }
    })
    members.value.sort((a, b) => a.position - b.position)
  }

  const lookupLinkedin = async (candidateId: string, linkedinUrl: string): Promise<LinkedinLookupResult | null> => {
    try {
      const response = await useFetch(`${getBaseUrl(candidateId)}/lookup-linkedin`).post<
        LinkedinLookupResult | LinkedinLookupError
      >({ linkedin_url: linkedinUrl })

      if ('error' in response) {
        return null
      }

      return response
    } catch {
      return null
    }
  }

  const reset = () => {
    members.value = []
  }

  return {
    members,
    loading,
    fetchTeamMembers,
    createTeamMember,
    updateTeamMember,
    deleteTeamMember,
    reorderTeamMembers,
    lookupLinkedin,
    reset,
  }
})
